import { Container, Divider } from "@material-ui/core";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  AutoCompleteComponent,
  CurrencyDisplay,
  CustomTable,
  ModalError,
  MonthYearPicker,
  Page,
  SearchTextInput,
} from "components";
import { ButtonGroupTop } from "components/atoms/Button";
import { HeaderTitle } from "layouts";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatDate, getErrors, wordCapitalize } from "utils";

const Billing = () => {
  const classes = globalStyles();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const [loadingPage, setLoadingPage] = useState(false);
  const [optionPublisher, setOptionPublisher] = useState([]);
  const [dataTable, setDataTable] = useState([]);
  const [tablePageCount, setTablePageCount] = useState(1);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    per_page: 10,
    search: "",
    date: null,
    publisher_id: "",
  });

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangeFilterMonth = date => {
    handleChangeQueryParams(date, "date");
  };
  const handleChangeRowsPerPage = event => {
    const value = event?.target?.value;
    handleChangeQueryParams(value, "per_page");
  };
  const handleChangeFilterPublisher = value => {
    handleChangeQueryParams(value, "publisher_id");
  };
  const handleChangeSearch = event => {
    const value = event?.target?.value;
    handleChangeQueryParams(value, "search");
  };

  const getOptionPublisher = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/publisher`, {
        headers,
      });
      const { data } = res?.data;
      const modifiedData = data?.map(item => ({
        label: item?.name,
        id: item?.publisher_id,
      }));
      setOptionPublisher(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getDataTable = async () => {
    const { date, ...restParams } = queryParams;
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/billing/publisher`, {
        headers,
        params: {
          ...restParams,
          month: date ? moment(date)?.format("MM") : "",
          year: date ? moment(date)?.format("YYYY") : "",
        },
      });
      const { data } = res?.data;
      setDataTable(data?.data);
      setTablePageCount(data?.total_page || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );

  useEffect(() => {
    getOptionPublisher();
  }, []);
  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Billing Publisher">
      <Container maxWidth={false}>
        <HeaderTitle title="Billing" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes.flatDivider} />
        <Grid container justifyContent="space-between" my={2}>
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search || ""}
              onChange={handleChangeSearch}
              disabled={loadingPage}
            />
          </Grid>
          <Grid item>
            <Grid container columnSpacing={1}>
              <Grid item>
                <MonthYearPicker
                  label="Period"
                  value={queryParams?.date || null}
                  onChange={handleChangeFilterMonth}
                  disabled={loadingPage}
                />
              </Grid>
              <Grid item>
                <AutoCompleteComponent
                  options={optionPublisher}
                  label="Publisher"
                  value={
                    optionPublisher.find(
                      option => option.id === queryParams?.publisher_id
                    ) || null
                  }
                  onChange={value => handleChangeFilterPublisher(value)}
                  width={220}
                  size="small"
                  disabled={loadingPage}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable || []}
          page={queryParams?.page || 1}
          pageSize={queryParams?.per_page || 10}
          pageCount={tablePageCount}
          handleChangePage={(_, value) =>
            handleChangeQueryParams(value, "page")
          }
          handleChangePageSize={handleChangeRowsPerPage}
          my={2}
        />
      </Container>
    </Page>
  );
};

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Billing",
    active: true,
  },
];
const columnTable = [
  {
    name: "transaction_number",
    title: "Invoice Number",
  },
  {
    name: "publisher",
    title: "Publisher",
  },
  {
    name: "date",
    title: "Date",
    renderText: date => formatDate(moment(date, "DD MMMM YYYY")),
  },
  {
    name: "period",
    title: "Period",
  },
  {
    name: "value",
    title: "Value",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay value={item} prefix="Rp" decimalScale={2} />
    ),
  },
  {
    name: "billing_value",
    title: "Billing Value",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay value={item} prefix="Rp" decimalScale={2} />
    ),
  },
  {
    name: "status",
    title: "Status",
    renderText: item => wordCapitalize(item),
  },
];
export default Billing;
